'use client';
import React, { useEffect, useState } from 'react';


const showcaseItems = [
{ image: "/assets/images/Ecom_envelope-1787035235342.jpeg", label: 'Ecom Envelopes', bg: 'bg-amber-50', alt: 'Brown kraft paper ecommerce mailing envelopes stacked together' },
{ image: "/assets/images/Use_AI_Image_Aug_18__2026__12_10_47-1787035801448.jpeg", label: 'Kraft Boxes', bg: 'bg-orange-50', alt: 'Sustainable kraft cardboard boxes for product packaging' },
{ image: "/assets/images/tote-1787035801487.jpeg", label: 'Cotton Bags', bg: 'bg-green-50', alt: 'Natural cotton tote bags for eco-friendly retail packaging' },
{ image: "/assets/images/Use_AI_Image_Aug_18__2026__12_19_27-1787035801430.jpeg", label: 'Glass Jars', bg: 'bg-teal-50', alt: 'Clear glass jars for sustainable product storage and packaging' },
{ image: "/assets/images/Use_AI_Image_Aug_18__2026__12_14_22-1787035485849.jpeg", label: 'Monocartons', bg: 'bg-lime-50', alt: 'Printed monocarton folding boxes for retail product packaging' },
{ image: "/assets/images/Use_AI_Image_Aug_18__2026__11_56_29-1787035235481.jpeg", label: 'Seed Paper', bg: 'bg-emerald-50', alt: 'Handmade seed paper sheets embedded with wildflower seeds' }];


export default function HeroBanner() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative w-full overflow-hidden bg-white min-h-[420px] md:min-h-[520px] lg:min-h-[580px]">
      {/* Subtle leaf/organic background shapes */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-primary/5" />
        <div className="absolute bottom-0 -left-16 w-72 h-72 rounded-full bg-secondary/40" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/3 opacity-30" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 lg:py-20 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Left: Text content */}
          <div
            className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`
            }>
            
            <span className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-wider">
              <span className="w-2 h-2 rounded-full bg-primary inline-block" />
              Eco-Conscious Packaging
            </span>
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-5 text-primary-dark"
              style={{ fontFamily: 'Fraunces, serif' }}>
              
              Custom packaging that{' '}
              <span className="text-primary italic">tells</span>{' '}
              your brand's story
            </h1>
            <p className="text-gray-600 text-base font-normal mb-7 leading-relaxed max-w-lg">
              From ecom boxes to conference kits — all crafted with the planet in mind. Zero virgin plastic. Fully customisable.
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              <a href="#products" className="btn-primary text-sm">
                Explore Products
              </a>
              <a href="#custom-packaging" className="btn-outline text-sm">
                Get Custom Quote
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-5 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Tree-Free Paper
              </span>
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Recycled materials
              </span>
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Custom Branding
              </span>
            </div>
          </div>

          {/* Right: Product showcase grid */}
          <div
            className={`transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`
            }>
            
            <div className="relative">
              {/* MOQ badge — top-right corner */}
              <div className="absolute -top-4 -right-4 z-10 bg-primary rounded-full w-20 h-20 flex flex-col items-center justify-center shadow-lg border-2 border-white">
                <span className="text-xs font-bold text-white leading-none">MOQ</span>
                <span className="text-xl font-black text-white leading-none">100*</span>
                <span className="text-xs font-bold text-white leading-none">units</span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {showcaseItems?.map((item) =>
                <div
                  key={item?.label}
                  className={`${item?.bg} rounded-2xl aspect-square flex flex-col items-center justify-center gap-2 border border-white shadow-sm overflow-hidden relative`}>
                  
                    <img
                    src={item?.image}
                    alt={item?.alt}
                    className="absolute inset-0 w-full h-full object-cover rounded-2xl" />
                  
                    <div className="absolute inset-0 bg-black/20 rounded-2xl" />
                  
                  </div>
                )}
              </div>

              {/* Shipping pill — below the grid */}
              <div className="flex flex-col items-center mt-4 gap-1">
                <div className="bg-primary text-white text-xs font-semibold px-5 py-2.5 rounded-full shadow-md whitespace-nowrap">
✓ STANDARD ORDERS SHIP IN 3–5 BUSINESS DAYS
                </div>
                <p className="text-xs text-gray-500 mt-1">* Custom orders will ship within 2–3 weeks.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}